import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useActiveOrg } from '../contexts/ActiveOrgContext'
import { useCapabilities } from '../auth/useCapabilities'

/**
 * Redirects away from an Event Planner page (to the home page) as soon as the
 * active organization no longer has the `eventplanner` feature enabled — e.g. the
 * user switches to a different org (via the org switcher) while still on an Event
 * Planner page. Without this, the page can keep showing stale data fetched for the
 * previous org instead of reflecting that the new org has no access.
 *
 * Waits for both the org's feature list and the caller's global capabilities to
 * finish loading before deciding, so the brief in-flight window right after
 * switching orgs isn't mistaken for "feature disabled" and doesn't bounce the user
 * out prematurely.
 */
export function useRequireEventPlannerFeature() {
  const navigate = useNavigate()
  const { activeOrgId, activeOrgFeatures, isActiveOrgFeaturesLoading } = useActiveOrg()
  const { canCreateEvent, isLoading: isCapabilitiesLoading } = useCapabilities()

  useEffect(() => {
    if (!activeOrgId || isActiveOrgFeaturesLoading || isCapabilitiesLoading) return
    if (!canCreateEvent || !activeOrgFeatures.includes('eventplanner')) {
      navigate('/', { replace: true })
    }
  }, [
    activeOrgId,
    activeOrgFeatures,
    isActiveOrgFeaturesLoading,
    canCreateEvent,
    isCapabilitiesLoading,
    navigate,
  ])
}
